import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Smart back navigation hook.
 *
 * Priority order:
 *   1. Tracked referrer (`location.state.from`) — set by `useNavigateWithFrom`
 *      / `LinkWithFrom` at the launch site.
 *   2. Browser back (`navigate(-1)`) when there is real in-app history.
 *   3. Caller-provided `fallbackRoute` (or contextual fallback resolver).
 *
 * Callers should prefer `useNavigateWithFrom()` for every in-app push that
 * lands on a back-button page so #1 fires instead of falling through to #3.
 */
export function useSmartBack(fallbackRoute: string = '/') {
  const navigate = useNavigate();
  const location = useLocation();

  // Re-read every render so we don't miss late-bound state (e.g. router
  // updates after suspense, BFCache restore, HMR).
  const fromState = (location.state as { from?: string } | null)?.from ?? null;

  // location.key === 'default' only on the very first entry of a fresh SPA
  // session (deep-link, refresh, share). Anything else means we have at
  // least one prior in-app entry that `navigate(-1)` can pop to.
  const hasInAppHistory = location.key !== 'default';

  const goBack = () => {
    if (fromState) {
      navigate(fromState);
      return;
    }
    if (hasInAppHistory) {
      navigate(-1);
      return;
    }
    navigate(fallbackRoute);
  };

  return {
    goBack,
    previousPath: fromState,
    hasPreviousRoute: hasInAppHistory || !!fromState,
    hasContextualBack: !!fromState,
  };
}
